import { t, PASSWORD_MIN_LENGTH, UUID, DateTimeISO, stripDefaults } from '../common.ts';

const MemberBaseShape = {
  username: t.String({ minLength: 3, maxLength: 64 }),
  phone: t.String({ minLength: 8, maxLength: 20 }),
  phoneCountryCode: t.String({ default: '+66' }),
  firstName: t.Optional(t.Union([t.String({ maxLength: 64 }), t.Null()])),
  lastName: t.Optional(t.Union([t.String({ maxLength: 64 }), t.Null()])),
  email: t.Optional(t.Union([t.String({ format: 'email' }), t.Null()])),
  birthday: t.Optional(t.Union([DateTimeISO, t.Null()])),
  groupId: UUID,
  referrerId: t.Optional(t.Union([UUID, t.Null()])),
  status: t.Union([t.Literal('active'), t.Literal('inactive')], { default: 'active' }),
  watchlist: t.Boolean({ default: false }),
  autoDeposit: t.Boolean({ default: true }),
  maxBankAccounts: t.Integer({ minimum: 1, maximum: 10, default: 4 }),
} as const;

export const MemberBase = t.Object(MemberBaseShape);

export const MemberView = t.Object(
  {
    ...MemberBaseShape,
    id: UUID,
    emailVerified: t.Boolean(),
    registeredIp: t.Union([t.String(), t.Null()]),
    registeredAt: DateTimeISO,
    createdBy: t.Union([UUID, t.Null()]),
  },
  { $id: 'MemberView' },
);

export const RegisterMemberRequest = t.Object(
  {
    phone: t.String({ minLength: 8, maxLength: 20, pattern: '^\\+[1-9]\\d{7,14}$' }),
    phoneCountryCode: t.String({ default: '+66' }),
    password: t.String({ minLength: PASSWORD_MIN_LENGTH, maxLength: 128 }),
    bankCode: t.String(),
    bankAccountNo: t.String(),
    bankAccountName: t.String(),
    referralCode: t.Optional(t.String()),
    turnstileToken: t.String(),
  },
  { $id: 'RegisterMemberRequest' },
);

// stripDefaults: otherwise Elysia injects the create-time defaults
// (status 'active', watchlist false, autoDeposit true, maxBankAccounts 4,
// phoneCountryCode '+66') into every PATCH that omits them.
export const UpdateMemberRequest = t.Partial(stripDefaults(MemberBase), {
  $id: 'UpdateMemberRequest',
});

export const MemberGroupView = t.Object(
  {
    id: UUID,
    code: t.String(),
    name: t.String(),
    color: t.Union([t.String(), t.Null()]),
    level: t.Integer(),
    autoPromote: t.Boolean(),
    memberCount: t.Integer(),
    isDefault: t.Boolean(),
  },
  { $id: 'MemberGroupView' },
);

export const MemberBankAccountView = t.Object(
  {
    id: UUID,
    memberId: UUID,
    bankCode: t.String(),
    accountNo: t.String(),
    accountName: t.String(),
    isPrimary: t.Boolean(),
    createdAt: DateTimeISO,
  },
  { $id: 'MemberBankAccountView' },
);

export const BlacklistEntryView = t.Object(
  {
    id: UUID,
    phone: t.Union([t.String(), t.Null()]),
    firstName: t.Union([t.String(), t.Null()]),
    lastName: t.Union([t.String(), t.Null()]),
    bankCode: t.Union([t.String(), t.Null()]),
    bankNo: t.Union([t.String(), t.Null()]),
    reason: t.String(),
    byAdmin: UUID,
    createdAt: DateTimeISO,
  },
  { $id: 'BlacklistEntryView' },
);
